import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import APIRequest from "../../API";
import * as APIPATHS from "../../API/path";
import toast from "react-hot-toast";
import {
  ExploreListResponse,
  ExploreInitialState,
} from "../../Config/interface.config";
import { RoleTypes } from "../../Constant/enums/roleTypes";

export interface GetCreatorListQueryParams {
  limit: number;
  page: number;
  isFilterBySubscriber?: boolean;
  role?: (typeof RoleTypes)[keyof typeof RoleTypes];
  fullname?: string;
  isAppend?: boolean;
}

interface ExploreFulfilledPayload extends ExploreListResponse {
  isAppend?: boolean;
}

const initialState: ExploreInitialState = {
  results: [],
  page: 1,
  limit: 10,
  hasMore: true,
  loading: false,
  isModalOpen: false,
};

export const fetchExploreList = createAsyncThunk<
  ExploreFulfilledPayload,
  GetCreatorListQueryParams
>(
  "creatorlist/fetchExploreList",
  async (requestPayload, { rejectWithValue }) => {
    try {
      const { isAppend, ...apiPayload } = requestPayload;
      const response: any = await APIRequest(
        APIPATHS.exploreList,
        {},
        apiPayload
      );

      if (response.error) throw new Error(response.error.message);

      return {
        ...response.data,
        isAppend,
      };
    } catch (error: any) {
      return rejectWithValue({ reason: error.message });
    }
  }
);

const exploreSlice = createSlice({
  name: "explore",
  initialState,
  reducers: {
    clearExploreResults: (state) => {
      state.results = [];
      state.page = 1;
      state.limit = 10;
      state.hasMore = true;
      state.loading = false;
    },

    restore: (state, action) => {
      const p = action.payload || {};

      state.results = Array.isArray(p.results) ? p.results : [];
      state.page = typeof p.page === "number" ? p.page : 1;
      state.limit = typeof p.limit === "number" ? p.limit : 10;
      state.hasMore =
        typeof p.hasMore === "boolean" ? p.hasMore : true;
      state.loading = false;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchExploreList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchExploreList.fulfilled, (state, action) => {
        state.loading = false;

        const { results, page, limit, hasMore, isAppend } =
          action.payload;

        if (isAppend) {
          state.results.push(...results);
        } else {
          state.results = results;
        }

        state.page = page;
        state.limit = limit;
        state.hasMore = hasMore;
      })
      .addCase(fetchExploreList.rejected, (state, action: any) => {
        state.loading = false;
        state.hasMore = false;
        toast.error(
          action.payload?.reason ?? "Failed to load creators"
        );
      });
  },
});

export const { clearExploreResults } = exploreSlice.actions;
export default exploreSlice.reducer;
